import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { StopLoading } from 'src/shared/state/main.action';
import { BaseResponse } from '../../shared/models/base-response.model';
import { Logout } from '../../shared/state/auth.action';
import { UtilService } from './util.service';

export const NOT_LOGGED_IN = 'Not Logged In';
export const UNAUTHORIZED = 'Unauthorized';
export const UNEXPECTED_BACKEND_RESPONSE = 'Unexpected Backend Response';

@Injectable({
	providedIn: 'root',
})
export class BackendService {
	constructor(
		private http: HttpClient,
		private store: Store,
		private utils: UtilService
	) {}

	/**
	 * Validate a BaseResponse message is the same as validMessage
	 *
	 * @param validMessage
	 */
	private validateResponse(
		response: BaseResponse,
		validMessage: string
	): BaseResponse {
		if (!BaseResponse.validResponseMessage(response, validMessage)) {
			throw new Error(UNEXPECTED_BACKEND_RESPONSE);
		}
		return response;
	}

	/**
	 * Handle HttpErrorResponses
	 *
	 * @param errorResponse
	 */
	private handleErrors(error: HttpErrorResponse | Error) {
		let errMsg: string;
		if (error instanceof HttpErrorResponse) {
			if (error.status === 500) {
				errMsg = `${error.status} - ${error.statusText}`;
			} else if (error.error) {
				if (error.error instanceof ArrayBuffer) {
					errMsg = this.getArrayBufferMessage(error.error);
				} else if (error.error instanceof Error) {
					errMsg = error.error.message;
				} else if (error.error.hasOwnProperty('error')) {
					errMsg = error.error.error;
				} else if (error.error.hasOwnProperty('message')) {
					errMsg = error.error.message;
				} else if (error.error.hasOwnProperty('title')) {
					errMsg = `${error.error.status} - ${error.error.title}`;
				} else {
					errMsg = error.error;
				}
			}
		} else if (error instanceof Error) {
			errMsg = error.message;
		}
		console.error(`Backend Request Error: ${errMsg}`, error);
		if (errMsg === NOT_LOGGED_IN || errMsg === UNAUTHORIZED) {
			this.store.dispatch(new Logout());
		} else {
			this.store.dispatch(new StopLoading());
			this.utils.errorMessage(errMsg);
		}
		return throwError(() => errMsg);
	}

	private getArrayBufferMessage(buffer: ArrayBuffer): string {
		const decoder = new TextDecoder('utf-8');
		const decoded: { message: string } = JSON.parse(decoder.decode(buffer));
		return decoded.message;
	}

	/**
	 * Base `post`
	 *
	 * @param url
	 * @param responseMessage
	 * @param request
	 */
	post<T extends BaseResponse>(
		url: string,
		responseMessage: string,
		request: any
	): Observable<T> {
		return this.http
			.post<T>(url, request, {
				withCredentials: true,
			})
			.pipe(
				tap((response) =>
					this.validateResponse(response, responseMessage)
				),
				catchError((error) => this.handleErrors(error))
			);
	}

	/**
	 * A `post` request intended to handle an `octet-stream` file download response
	 *
	 * @param url
	 * @param request
	 */
	download(url: string, request: any): Observable<Blob> {
		return this.http
			.post(url, request, {
				withCredentials: true,
				observe: 'response',
				responseType: 'arraybuffer',
			})
			.pipe(
				map((res: any) => {
					if (res.ok) {
						const blob: Blob = new Blob([res.body], {
							type: 'application/octet-stream',
						});
						return blob;
					} else {
						throw new Error('There was a download error.');
					}
				}),
				catchError((error) => this.handleErrors(error))
			);
	}
}
